/* import __COLOCATED_TEMPLATE__ from './channel-limit-settings.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import { action } from '@ember/object';
import { debounce } from '@ember/runloop';
import type AppConversationCapacityLimit from 'embercom/models/app-conversation-capacity-limit';

interface Signature {
  Args: {
    model: AppConversationCapacityLimit;
  };
}

export default class ChannelLimitSettings extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.materializeIfStale();
  }

  // Materialize `channelLimits` when the model is in a stale shape — enabled with
  // null or partial keys. Called at construction (legacy records) and after
  // `rollbackAttributes()` (failed save), since rollback can restore the model to
  // the same stale shape and the constructor only runs once.
  private materializeIfStale() {
    let limits = this.model.channelLimits;
    if (this.model.channelLimitsEnabled && (limits?.email == null || limits?.messenger == null)) {
      this.model.channelLimits = this.materializeChannelLimits();
    }
  }

  get model() {
    return this.args.model;
  }

  get channelLimitsEnabled() {
    return this.model.channelLimitsEnabled;
  }

  // Pre-materialization, the inputs display `capacityLimit` as a preview of the
  // inherited default. As soon as the user toggles channel limits on or edits
  // either input, `channelLimits` becomes a concrete object and these getters
  // return its values — at that point further `capacityLimit` changes no longer
  // affect what the user sees or what is persisted.
  get emailLimit() {
    return this.model.channelLimits?.email ?? this.model.capacityLimit;
  }

  get messengerLimit() {
    return this.model.channelLimits?.messenger ?? this.model.capacityLimit;
  }

  get isValid() {
    return this.model.validations.isValid;
  }

  // Materializes `channelLimits` to a concrete `{ email, messenger }` object with
  // numbers in both keys. Per-key `??` (rather than spread-with-defaults) handles
  // records that load with one key set and the other null — those would otherwise
  // let the `??` fallback in the getters keep firing for the null channel,
  // reintroducing the binding leak this PR fixes.
  private materializeChannelLimits(overrides: Record<string, number> = {}) {
    let existing = this.model.channelLimits;
    return {
      email: existing?.email ?? this.model.capacityLimit,
      messenger: existing?.messenger ?? this.model.capacityLimit,
      ...overrides,
    };
  }

  @action async toggleChannelLimitsEnabled() {
    this.model.channelLimitsEnabled = !this.model.channelLimitsEnabled;
    if (this.model.channelLimitsEnabled) {
      this.model.channelLimits = this.materializeChannelLimits();
    }
    await this.save();
  }

  @action updateEmailLimit(event: Event) {
    let value = parseInt((event.target as HTMLInputElement).value, 10);
    if (isNaN(value) || value < 0) {
      return;
    }
    this.model.channelLimits = this.materializeChannelLimits({ email: value });
    this.debouncedSave();
  }

  @action updateMessengerLimit(event: Event) {
    let value = parseInt((event.target as HTMLInputElement).value, 10);
    if (isNaN(value) || value < 0) {
      return;
    }
    this.model.channelLimits = this.materializeChannelLimits({ messenger: value });
    this.debouncedSave();
  }

  @action async validate() {
    this.model.errors.clear();

    let { validations } = await this.model.validations.validate();
    if (!validations.isValid) {
      validations.errors.forEach((error: any) => {
        this.model.errors.add(error.attribute, error.message);
      });
    }
  }

  @action async debouncedSave(debounceTime = 800) {
    await this.validate();
    if (this.isValid) {
      debounce(this, this.save, debounceTime);
    }
  }

  @action async save() {
    await this.validate();
    if (!this.isValid) {
      return;
    }

    try {
      await this.model.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('settings.workload-management.update-success-notification'),
      );
    } catch (_: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('settings.workload-management.update-fail-notification'),
      );
      this.model.rollbackAttributes();
      this.materializeIfStale();
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox::WorkloadManagement::ChannelLimitSettings': typeof ChannelLimitSettings;
    'inbox/workload-management/channel-limit-settings': typeof ChannelLimitSettings;
  }
}
